const pendingWorkspacePersistence = new Set<Promise<unknown>>();
const RETRYABLE_WORKSPACE_PERSISTENCE_STATUSES = new Set([502, 503, 504]);
const WORKSPACE_PERSISTENCE_RETRY_DELAY_MS = 150;

export function trackWorkspacePersistence<T>(operation: Promise<T>): Promise<T> {
  let trackedOperation: Promise<T>;
  trackedOperation = operation.finally(() => {
    pendingWorkspacePersistence.delete(trackedOperation);
  });
  pendingWorkspacePersistence.add(trackedOperation);
  return trackedOperation;
}

export async function fetchWorkspacePersistence(
  input: RequestInfo | URL,
  init: RequestInit,
  isCurrent: () => boolean = () => true,
): Promise<Response> {
  let lastError: unknown;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (!isCurrent()) throw new Error("Workspace owner changed");
    try {
      const response = await fetch(input, init);
      if (!isCurrent()) throw new Error("Workspace owner changed");
      if (!RETRYABLE_WORKSPACE_PERSISTENCE_STATUSES.has(response.status) || attempt === 1) {
        return response;
      }
    } catch (error) {
      lastError = error;
      if (!isCurrent() || attempt === 1) throw error;
    }

    await new Promise((resolve) => setTimeout(resolve, WORKSPACE_PERSISTENCE_RETRY_DELAY_MS));
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Workspace persistence request failed");
}

/** Wait briefly for saves that started before the sign-out boundary. */
export async function waitForWorkspacePersistence(timeoutMs = 3_000): Promise<boolean> {
  const pending = Array.from(pendingWorkspacePersistence);
  if (pending.length === 0) return true;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.allSettled(pending).then(() => true),
      new Promise<boolean>((resolve) => {
        timeoutId = setTimeout(() => resolve(false), Math.max(1, timeoutMs));
      }),
    ]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}
